import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
// TODO: remove once we fully migrate to RTG and remove the subscribeMore
import { assocPath, isEmpty } from 'ramda'
import { notification } from 'antd'
import { useQuery } from '@apollo/client'
import { useSound } from 'use-sound'

import NOTIFICATIONS from 'apollo/queries/NOTIFICATIONS'
// TODO: remove once we fully migrate to RTG
import NOTIFICATIONS_EVENTS from 'apollo/subscriptions/NOTIFICATIONS_EVENTS'

import notificationSound from 'assets/sounds/notification.mp3'
import alarmSound from 'assets/sounds/alarm.mp3'

import { playSound } from 'utils/helpers/soundHelper'
import { groupByKey } from 'utils/helpers'
import { getFromSessionStorage } from 'utils/sessionStorage'
import { useSubscription as useRtgSubscription } from '@rightway/websocket-multiplexer'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { prototimeToDate } from 'utils/prototime'
import { NotificationsContext } from './NotificationsContext'
import {
  CHAT_REPLY_TYPE,
  CANCEL_APPOINTMENT_TYPE,
  ALARM_TYPE,
  ASSIGNED_TYPE,
  TASK_TYPE,
  TASK_ASSIGNED_TYPE,
  BULK_CREATE_TICKETS,
  BULK_UPLOAD_CATEGORIES_TYPE,
  BENEFIT_CATEGORIES_BULK_CREATE,
  BENEFIT_GROUPS_BULK_CREATE,
  BENEFITS_BULK_CREATE,
  INSURANCE_PLAN_BULK_UPLOAD,
  INSURANCE_PLAN_CLONED,
  INTERACTIONS_BULK_CREATE,
  USER_TAGGED,
  TICKET_DELIVERABLES_BULK_CREATE,
  TICKET_BULK_ASSIGN_TO_PERFORMER,
  TICKET_BULK_ASSIGN_TO_QUEUE,
  TICKET_BULK_ASSIGN_TO_RECIPIENT,
  TICKET_BULK_CHANGE_STATUS,
  BENEFIT_ORGS_BULK_UPLOAD
} from './constants'
import { isTimedType, openNotification, getFilteredTimedTypes } from './helpers'

const INTERVAL = 2 * 1000 // 2s
const HEADER_HEIGHT = 77 // header 65px + margin 12px

const useEventBus = () => {
  const listeners = useRef({})

  const subscribe = useCallback((eventName, callback) => {
    if (!listeners.current[eventName]) listeners.current[eventName] = []
    listeners.current[eventName].push(callback)

    return () => {
      listeners.current[eventName] = listeners.current[eventName].filter(
        cb => cb !== callback
      )
    }
  }, [])

  const broadcast = useCallback((eventName, data) => {
    if (listeners.current[eventName]) {
      listeners.current[eventName].forEach(callback => {
        try {
          callback(data)
        } catch (error) {
          console.error(`Error in event listener for ${eventName}:`, error)
        }
      })
    }
  }, [])

  return { subscribe, broadcast }
}

const NotificationsProvider = ({ children, tabId }) => {
  const [playAlarm] = useSound(alarmSound)
  const [playNotification] = useSound(notificationSound)
  const activeTabId = getFromSessionStorage('activeTabId')()
  const isActiveTab = activeTabId === tabId
  const [api, contextHolder] = notification.useNotification({
    top: HEADER_HEIGHT
  })
  const { subscribe, broadcast } = useEventBus()
  const { subscriptionMessages, subscribeToEvent } = useRtgSubscription()
  const { enableChatUpdateEvent } = useFlags()

  const { data, subscribeToMore } = useQuery(NOTIFICATIONS)

  // this is the initial set of notifications from the query
  const initialNotifications = useMemo(
    () => data?.notifications?.nodes ?? [],
    [data]
  )

  const previousNotificationIdsRef = useRef(null)

  const notifications = useMemo(() => {
    let notificationsCopy = initialNotifications.slice()

    const hasNoSubscriptionMessages = subscriptionMessages
      .values()
      .every(arr => arr.length === 0)

    // TODO: once we remove the need for enableChatUpdateEvent FF update this line
    if (enableChatUpdateEvent === false || hasNoSubscriptionMessages) {
      return notificationsCopy
    }

    // check to see if there any notification has been deleted
    const deletedKey =
      subscriptionMessages
        .keys()
        .find(key => key.startsWith('notificationDeleted')) ?? ''

    const createdKey =
      subscriptionMessages
        .keys()
        .find(key => key.startsWith('notificationCreated')) ?? ''

    let deletedIds = new Set()

    if (deletedKey.length > 0) {
      const deletedEvents = subscriptionMessages.get(deletedKey)

      // if we have deletedEvents we want to remove them from the initial list of notifications
      if (deletedEvents.length > 0) {
        deletedIds = new Set(deletedEvents.map(event => event.payload.data.id))
        notificationsCopy = notificationsCopy.filter(n => !deletedIds.has(n.id))
      }
    }

    // append new notification to list, this will be a shim but should be ok for UI purposes
    if (createdKey.length > 0) {
      const newNotifications = subscriptionMessages.get(createdKey)
      newNotifications.forEach(notif => {
        const { data: eventData } = notif.payload

        const isNewNotification =
          !deletedIds.has(eventData.id) &&
          notificationsCopy.every(previous => previous.id !== eventData.id)

        if (isNewNotification) {
          const type = eventData.type.toLowerCase()
          notificationsCopy.push({
            id: eventData.id,
            type,
            source_id: eventData.sourceId,
            source_type: eventData.sourceType,
            data: eventData.data,
            created_at: prototimeToDate(eventData.createdAt)
          })
        }
      })
    }

    return notificationsCopy
  }, [initialNotifications, subscriptionMessages, enableChatUpdateEvent])

  // alert for newly created notifications
  useEffect(() => {
    // Only run this effect when FF is enabled. Remove once we remove flag
    if (enableChatUpdateEvent === false) {
      return
    }

    // seed with current IDs on first render, don't alert
    if (previousNotificationIdsRef.current === null) {
      previousNotificationIdsRef.current = new Set(notifications.map(n => n.id))
      return
    }

    const currentIds = new Set(notifications.map(n => n.id))

    notifications.forEach(n => {
      if (!previousNotificationIdsRef.current.has(n.id)) {
        broadcast(`${n.type}:created`)
        if (!isTimedType(n)) {
          openNotification(n, api)
          if (isActiveTab) playSound(playNotification)
        }
      }
    })

    previousNotificationIdsRef.current = currentIds
  }, [
    enableChatUpdateEvent,
    notifications,
    api,
    isActiveTab,
    broadcast,
    playNotification
  ])

  // TODO: remove once we fully migrate to RTG
  const subscribeToNavigatorEvents = useCallback(() => {
    if (subscribeToMore) {
      return subscribeToMore({
        document: NOTIFICATIONS_EVENTS,
        updateQuery: (currentData, { subscriptionData }) => {
          if (isEmpty(currentData)) return currentData

          const {
            data: {
              notification_events: { event_type, notification: item }
            }
          } = subscriptionData

          const items = currentData?.notifications?.nodes || []

          broadcast(`${item.type}:${event_type}`)

          if (event_type === 'created') {
            if (!isTimedType(item)) {
              openNotification(item, api)
              if (isActiveTab) playSound(playNotification)
            }

            return assocPath(
              ['notifications', 'nodes'],
              [...items, item],
              currentData
            )
          }

          if (event_type === 'deleted') {
            const newNotifications = items.filter(i => i.id !== item.id)
            return assocPath(
              ['notifications', 'nodes'],
              [...newNotifications],
              currentData
            )
          }

          return currentData
        }
      })
    }
  }, [subscribeToMore, playNotification, isActiveTab])

  useEffect(() => {
    if (enableChatUpdateEvent) {
      return
    }
    const unsubscribe = subscribeToNavigatorEvents()
    return () => unsubscribe()
  }, [subscribeToNavigatorEvents, enableChatUpdateEvent])

  const generalNotifications = useMemo(
    () =>
      notifications.filter(i =>
        [
          CANCEL_APPOINTMENT_TYPE,
          ASSIGNED_TYPE,
          TASK_ASSIGNED_TYPE,
          BULK_CREATE_TICKETS,
          BULK_UPLOAD_CATEGORIES_TYPE,
          INSURANCE_PLAN_BULK_UPLOAD,
          BENEFIT_CATEGORIES_BULK_CREATE,
          BENEFIT_GROUPS_BULK_CREATE,
          BENEFITS_BULK_CREATE,
          INSURANCE_PLAN_CLONED,
          INTERACTIONS_BULK_CREATE,
          USER_TAGGED,
          TICKET_DELIVERABLES_BULK_CREATE,
          TICKET_BULK_ASSIGN_TO_PERFORMER,
          TICKET_BULK_ASSIGN_TO_QUEUE,
          TICKET_BULK_ASSIGN_TO_RECIPIENT,
          TICKET_BULK_CHANGE_STATUS,
          BENEFIT_ORGS_BULK_UPLOAD
        ].includes(i.type)
      ),
    [notifications]
  )

  const chatReplies = useMemo(
    () => notifications.filter(i => [CHAT_REPLY_TYPE].includes(i.type)),
    [notifications]
  )

  const groupedReplies = useMemo(
    () => groupByKey(chatReplies, 'source_id'),
    [chatReplies]
  )

  const alarms = useMemo(
    () => notifications.filter(i => i.type === ALARM_TYPE),
    [notifications]
  )

  const tasks = useMemo(
    () => notifications.filter(i => i.type === TASK_TYPE),
    [notifications]
  )

  const [activeAlarms, setActiveAlarms] = useState(alarms)
  const [activeTasks, setActiveTasks] = useState(tasks)
  const [isTimedReady, setIsTimedReady] = useState(false)

  const showNewTimed = useCallback(
    newTimed => {
      const oldIds = [...activeAlarms, ...activeTasks].map(i => i.id)

      const newItems = newTimed.filter(i => !oldIds.includes(i.id))

      // avoid initial population popups
      if (!isTimedReady) return false

      if (newItems?.length) {
        newItems.forEach(i => openNotification(i, api))

        if (isActiveTab) playSound(playAlarm)
      }
    },
    [activeAlarms, activeTasks, isTimedReady, playAlarm, isActiveTab]
  )

  const refreshTimed = useCallback(() => {
    const newAlarms = getFilteredTimedTypes(alarms)
    setActiveAlarms(newAlarms)
    const newTasks = getFilteredTimedTypes(tasks)
    setActiveTasks(newTasks)

    showNewTimed([...newAlarms, ...newTasks])

    if (!isTimedReady) setIsTimedReady(true)
  }, [alarms, tasks, showNewTimed, isTimedReady])

  useEffect(() => {
    const interval = setInterval(() => {
      refreshTimed()
    }, INTERVAL)
    return () => clearInterval(interval)
  }, [refreshTimed])

  useEffect(() => {
    const unsubscribeFromNotificationCreated = subscribeToEvent({
      eventName: 'notificationCreated',

      params: {},
      dynamicParamKeys: ['id']
    })

    const unsubscribeFromNotificationDeleted = subscribeToEvent({
      eventName: 'notificationDeleted'
    })

    return () => {
      unsubscribeFromNotificationCreated()
      unsubscribeFromNotificationDeleted()
    }
  }, [subscribeToEvent])

  const value = useMemo(
    () => ({
      generalNotifications,
      groupedReplies,
      chatUnreadCount: chatReplies.length,
      taskUnreadCount: activeTasks.length,
      activeAlarms: [...activeAlarms, ...activeTasks],
      broadcast,
      subscribe
    }),
    [
      activeAlarms,
      activeTasks,
      generalNotifications,
      groupedReplies,
      chatReplies,
      broadcast,
      subscribe
    ]
  )

  return (
    <NotificationsContext.Provider value={value}>
      {contextHolder}
      {children}
    </NotificationsContext.Provider>
  )
}

NotificationsProvider.propTypes = {
  tabId: PropTypes.string.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
}

export default NotificationsProvider
